'use client'

// src/RadioGroup/RadioGroup.tsx
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { twMerge } from "tailwind-merge";
import { SuccessIcon } from "@wbk/svg";

// src/RadioGroup/RadioGroup.classes.tsx
var RADIO_GROUP_CLASSES = {
  primary: "has-[[data-state=checked]]:border-primary hover:border-primary/70 focus:border-primary-400 has-[[data-state=checked]]:border-2 has-[:disabled]:opacity-70",
  secondary: "has-[[data-state=checked]]:border-secondary hover:border-secondary/70 focus:border-secondary-400 has-[[data-state=checked]]:border-2 has-[:disabled]:opacity-70",
  tertiary: "has-[[data-state=checked]]:border-tertiary hover:border-tertiary/70 focus:border-tertiary-400 has-[[data-state=checked]]:border-2 has-[:disabled]:opacity-70",
  action: "has-[[data-state=checked]]:border-action hover:border-action/70 focus:border-action-400 has-[[data-state=checked]]:border-2 has-[:disabled]:opacity-70",
  white: "has-[[data-state=checked]]:border-body-dark hover:border-body-dark/70 focus:border-body-dark/70 has-[[data-state=checked]]:border-2 has-[:disabled]:opacity-70",
  black: "has-[[data-state=checked]]:border-body-light hover:border-body-light/70 focus:border-body-light/70 has-[[data-state=checked]]:border-2 has-[:disabled]:opacity-70"
};
var RADIO_ITEM_CLASSES = {
  primary: "outline-primary data-[state=checked]:border-primary",
  secondary: "outline-secondary data-[state=checked]:border-secondary",
  tertiary: "outline-tertiary data-[state=checked]:border-tertiary",
  action: "outline-action data-[state=checked]:border-action",
  white: "outline-body-dark data-[state=checked]:border-body-dark",
  black: "outline-body-light data-[state=checked]:border-body-light"
};
var INDICATOR_CLASSES = {
  default: {
    primary: 'after:block after:h-[14px] after:w-[14px] after:rounded-full after:content-[""] after:bg-primary',
    secondary: 'after:block after:h-[14px] after:w-[14px] after:rounded-full after:content-[""] after:bg-secondary',
    tertiary: 'after:block after:h-[14px] after:w-[14px] after:rounded-full after:content-[""] after:bg-tertiary',
    action: 'after:block after:h-[14px] after:w-[14px] after:rounded-full after:content-[""] after:bg-action',
    white: 'after:block after:h-[14px] after:w-[14px] after:rounded-full after:content-[""] after:bg-body-dark',
    black: 'after:block after:h-[14px] after:w-[14px] after:rounded-full after:content-[""] after:bg-body-light'
  },
  check: {
    primary: "bg-primary text-primary-contrast [&>svg]:w-full [&>svg]:h-full [&>svg]:rounded-full [&>svg]:p-1",
    secondary: "bg-secondary text-secondary-contrast [&>svg]:w-full [&>svg]:h-full [&>svg]:rounded-full [&>svg]:p-1",
    tertiary: "bg-tertiary text-tertiary-contrast [&>svg]:w-full [&>svg]:h-full [&>svg]:rounded-full [&>svg]:p-1",
    action: "bg-action text-action-contrast [&>svg]:w-full [&>svg]:h-full [&>svg]:rounded-full [&>svg]:p-1",
    white: "bg-body-dark text-body-light [&>svg]:w-full [&>svg]:h-full [&>svg]:rounded-full [&>svg]:p-1",
    black: "bg-body-light text-body-dark [&>svg]:w-full [&>svg]:h-full [&>svg]:rounded-full [&>svg]:p-1"
  }
};

// src/RadioGroup/RadioGroup.tsx
import { jsx, jsxs } from "react/jsx-runtime";
var RadioGroup = ({
  name,
  label,
  options,
  value,
  onChange,
  error,
  disabled,
  className,
  groupClassName,
  required,
  dir,
  hideErrorSpace = true,
  theme = "primary",
  indicator = "default",
  ref
}) => {
  return /* @__PURE__ */ jsxs(
    RadioGroupPrimitive.Root,
    {
      name,
      value,
      onValueChange: onChange,
      required,
      disabled,
      dir,
      children: [
        /* @__PURE__ */ jsx(
          "div",
          {
            className: "text-text data-[disabled=true]:text-text/60 block pb-1",
            "data-disabled": disabled,
            children: label
          }
        ),
        /* @__PURE__ */ jsx("div", { className: twMerge("scroll-mt-36 space-y-2", groupClassName), ref, tabIndex: -1, children: options.map((op) => /* @__PURE__ */ jsxs(
          "div",
          {
            onPointerUp: () => {
              if (!disabled && !op.disabled) {
                onChange(op.value);
              }
            },
            className: twMerge(
              "border-paper group flex cursor-pointer items-center justify-between gap-2 rounded-xl border-2 p-4",
              RADIO_GROUP_CLASSES[theme],
              "has-[:disabled]:hover:border-paper has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50",
              className
            ),
            children: [
              /* @__PURE__ */ jsx(
                "label",
                {
                  className: `w-full grow ${op.disabled || disabled ? "cursor-not-allowed" : "cursor-pointer"}`,
                  htmlFor: `${name}_${op.value}`,
                  children: op.text || op.value
                }
              ),
              /* @__PURE__ */ jsx(
                RadioGroupPrimitive.Item,
                {
                  className: twMerge(
                    "border-input-border disabled:border-input-disabled peer h-[24px] w-[24px] shrink-0 rounded-full border-2 aria-checked:data-[check=true]:border-none",
                    RADIO_ITEM_CLASSES[theme]
                  ),
                  value: op.value,
                  checked: value === op.value,
                  id: `${name}_${op.value}`,
                  "data-check": indicator === "check",
                  "data-testid": `ui_radio_${name}_${op.value}`,
                  disabled: op.disabled || disabled,
                  children: /* @__PURE__ */ jsx(
                    RadioGroupPrimitive.Indicator,
                    {
                      className: twMerge(
                        "flex items-center justify-center rounded-full",
                        INDICATOR_CLASSES[indicator][theme]
                      ),
                      children: indicator === "check" && /* @__PURE__ */ jsx(SuccessIcon, {})
                    }
                  )
                }
              )
            ]
          },
          op.value
        )) }),
        (typeof error === "string" && !!error || !hideErrorSpace) && /* @__PURE__ */ jsx(
          "p",
          {
            className: `text-error my-1 min-h-[16px] text-xs ${error ? "visible" : "text-transparent"}`,
            children: error
          }
        )
      ]
    }
  );
};
var RadioGroup_default = RadioGroup;

// src/RadioGroup/RadioGroup.types.tsx
var RADIO_GROUP_THEMES = [
  "primary",
  "secondary",
  "tertiary",
  "action",
  "white",
  "black"
];

export {
  RadioGroup_default,
  RADIO_GROUP_THEMES
};
//# sourceMappingURL=chunk-WPD4VPMZ.mjs.map